import { classNames } from 'shared/lib/classNames/classNames';
import React, { MutableRefObject, useRef } from 'react';
import { Link } from 'gatsby';
import Logo from 'shared/assets/svg/logo.inline.svg';
import LogoMob from 'shared/assets/svg/logoMob.inline.svg';
import { RoutePath } from 'shared/config/routeConfig/routeConfig';
import { Button } from 'shared/ui/Button/Button';
import { Login } from 'shared/assets/gradientSvg/login/Login';
import { HeaderLink } from 'widgets/ui/Header/ui/HeaderLink/HeaderLink';
import { ThemeSwitcher } from 'shared/ui/ThemeSwitcher/ThemeSwitcher';
import { OpenNavButton } from 'features/MobileNav';
import SubMenu from 'shared/assets/img/directionSub.jpg';
import { ModalPositionEnum } from 'shared/ui/Modal/Modal';
import * as cls from './Header.module.css';
import { HeaderItemsList } from '../../model/items';

interface HeaderProps {
    className?: string,
}

export const Header = React.memo((props: HeaderProps) => {
    const { className } = props;
    const [subMenu, setSubMenu] = React.useState(false);
    const timerRef = useRef() as MutableRefObject<ReturnType<typeof setTimeout>>;

    const triggerShowSubMenu = React.useCallback(() => {
        clearTimeout(timerRef.current);
        setSubMenu(true);
    }, []);

    const triggerHideSubMenu = React.useCallback(() => {
        timerRef.current = setTimeout(() => {
            setSubMenu(false);
        }, 300);
    }, []);

    React.useEffect(() => {
        clearTimeout(timerRef.current);
        if (subMenu) {
            return document.querySelector('html')?.classList.add('showsubmenu');
        }

        return document.querySelector('html')?.classList.remove('showsubmenu');
    }, [subMenu]);

    const itemsList = React.useMemo(() => HeaderItemsList.map((item) => (
        <li
            className="h-full"
            key={item.path}
        >
            <HeaderLink
                text={item.text}
                to={item.path}
            />
        </li>

    )), []);

    return (
        <header
            className={classNames(cls.header, {}, [
                className,
                'flex items-center bg-whitefull dark:bg-bluegrey1000 ',
            ])}
        >
            <nav className="container flex items-center lg:grid grid-cols-12 gap-5 h-full">
                <Link
                    to={RoutePath.main}
                    className="w-fit block col-span-2"
                    itemProp="url"
                    title="Главная страница"
                    aria-label="Главная страница"
                >
                    <Logo
                        className={classNames(cls.logo, {}, [
                            className,
                            'logo hidden sm:block',
                        ])}
                    />
                    <LogoMob
                        className={classNames(cls.logo, {}, [
                            className,
                            'logo sm:hidden',
                        ])}
                    />
                </Link>

                <ul className="hidden lg:flex col-span-8 justify-end h-full -mr-3">
                    <li className={classNames(cls.submenuContainer, {}, ['h-full'])}>
                        <HeaderLink className={cls.subMenuLink} disabled text="Курсы" to="/directions/" onMouseEnter={triggerShowSubMenu} onMouseLeave={triggerHideSubMenu} />
                        <span className={classNames(cls.submenu, { [cls.showSub]: subMenu }, [])}>
                            <span className="submenu-2 bg-whitefull dark:bg-bluegrey1000 p-4 rounded-xl flex gap-[0.875rem]" onMouseEnter={triggerShowSubMenu} onMouseLeave={triggerHideSubMenu}>
                                <span className="rounded-xl overflow-hidden" style={{ width: '154px', height: '132px' }}>
                                    <img src={SubMenu} alt="Курсы" className="" />
                                </span>
                                <ul className="text-15med">
                                    {/* --- ДО --- */}
                                    <li>
                                        <Link onClick={() => setSubMenu(false)} to="/directions/svet" className="rounded-[0.375rem] p-2 hover:bg-blue50 dark:hover:bg-bluegrey700 transition-colors block whitespace-nowrap">Светодизайнер. Архитектурное освещение. Введение в профессию</Link>
                                    </li>
                                    <li>
                                        <Link onClick={() => setSubMenu(false)} to="/directions/mp_o_prof_do" className="rounded-[0.375rem] p-2 hover:bg-blue50 dark:hover:bg-bluegrey700 transition-colors block whitespace-nowrap">Менеджер проектных продаж. Введение в профессию</Link>
                                    </li>

                                    {/* --- ПП --- */}
                                    <li>
                                        <Link onClick={() => setSubMenu(false)} to="/directions/svet_professional" className="rounded-[0.375rem] p-2 hover:bg-blue50 dark:hover:bg-bluegrey700 transition-colors block whitespace-nowrap">Светодизайнер (ПП)</Link>
                                    </li>
                                    <li>
                                        <Link onClick={() => setSubMenu(false)} to="/directions/manager" className="rounded-[0.375rem] p-2 hover:bg-blue50 dark:hover:bg-bluegrey700 transition-colors block whitespace-nowrap">Менеджер проектных продаж</Link>
                                    </li>
                                    <li>
                                        <Link onClick={() => setSubMenu(false)} to="/directions/main_engineer" className="rounded-[0.375rem] p-2 hover:bg-blue50 dark:hover:bg-bluegrey700 transition-colors block whitespace-nowrap">Главный инженер проекта (ГИП)</Link>
                                    </li>
                                    <li>
                                        <Link onClick={() => setSubMenu(false)} to="/directions/tender_pp" className="rounded-[0.375rem] p-2 hover:bg-blue50 dark:hover:bg-bluegrey700 transition-colors block whitespace-nowrap">Специалист по тендерам</Link>
                                    </li>

                                    {/* --- ПК --- */}
                                    <li>
                                        <Link onClick={() => setSubMenu(false)} to="/directions/architectual" className="rounded-[0.375rem] p-2 hover:bg-blue50 dark:hover:bg-bluegrey700 transition-colors block whitespace-nowrap">Архитектурный светодизайн</Link>
                                    </li>
                                    <li>
                                        <Link onClick={() => setSubMenu(false)} to="/directions/landscape" className="rounded-[0.375rem] p-2 hover:bg-blue50 dark:hover:bg-bluegrey700 transition-colors block whitespace-nowrap">Ландшафтный светодизайн</Link>
                                    </li>
                                    <li>
                                        <Link onClick={() => setSubMenu(false)} to="/directions/svetotech" className="rounded-[0.375rem] p-2 hover:bg-blue50 dark:hover:bg-bluegrey700 transition-colors block whitespace-nowrap">Светотехника</Link>
                                    </li>
                                    <li>
                                        <Link onClick={() => setSubMenu(false)} to="/directions/prprod_pc" className="rounded-[0.375rem] p-2 hover:bg-blue50 dark:hover:bg-bluegrey700 transition-colors block whitespace-nowrap">Подготовка к проектным продажам</Link>
                                    </li>
                                    <li>
                                        <Link onClick={() => setSubMenu(false)} to="/directions/lidogen_pc" className="rounded-[0.375rem] p-2 hover:bg-blue50 dark:hover:bg-bluegrey700 transition-colors block whitespace-nowrap">Проектные продажи. Лидогенерация</Link>
                                    </li>
                                    <li>
                                        <Link onClick={() => setSubMenu(false)} to="/directions/coversion_pc" className="rounded-[0.375rem] p-2 hover:bg-blue50 dark:hover:bg-bluegrey700 transition-colors block whitespace-nowrap">Проектные продажи. Конверсия и переговоры</Link>
                                    </li>
                                    <li>
                                        <Link onClick={() => setSubMenu(false)} to="/directions/postsales_pc" className="rounded-[0.375rem] p-2 hover:bg-blue50 dark:hover:bg-bluegrey700 transition-colors block whitespace-nowrap">Постпродажное сопровождение</Link>
                                    </li>
                                    <li>
                                        <Link onClick={() => setSubMenu(false)} to="/directions/tender_zak" className="rounded-[0.375rem] p-2 hover:bg-blue50 dark:hover:bg-bluegrey700 transition-colors block whitespace-nowrap">Основы работы с тендерами и закупками</Link>
                                    </li>
                                    <li>
                                        <Link onClick={() => setSubMenu(false)} to="/directions/tender_sf" className="rounded-[0.375rem] p-2 hover:bg-blue50 dark:hover:bg-bluegrey700 transition-colors block whitespace-nowrap">Основы участия в тендерах в сфере светодизайна</Link>
                                    </li>
                                    <li>
                                        <Link onClick={() => setSubMenu(false)} to="/directions/zayavka" className="rounded-[0.375rem] p-2 hover:bg-blue50 dark:hover:bg-bluegrey700 transition-colors block whitespace-nowrap">Подготовка и подача заявок на ЭТП</Link>
                                    </li>
                                    <li>
                                        <Link onClick={() => setSubMenu(false)} to="/directions/instruments" className="rounded-[0.375rem] p-2 hover:bg-blue50 dark:hover:bg-bluegrey700 transition-colors block whitespace-nowrap">Инструменты работы тендерного отдела</Link>
                                    </li>
                                    <li>
                                        <Link onClick={() => setSubMenu(false)} to="/directions/gip" className="rounded-[0.375rem] p-2 hover:bg-blue50 dark:hover:bg-bluegrey700 transition-colors block whitespace-nowrap">Основные понятия профессии ГИП. Роль ГИПа в проекте</Link>
                                    </li>
                                    <li>
                                        <Link onClick={() => setSubMenu(false)} to="/directions/engi_pc" className="rounded-[0.375rem] p-2 hover:bg-blue50 dark:hover:bg-bluegrey700 transition-colors block whitespace-nowrap">Инженерные изыскания</Link>
                                    </li>
                                    <li>
                                        <Link onClick={() => setSubMenu(false)} to="/directions/preprojecting" className="rounded-[0.375rem] p-2 hover:bg-blue50 dark:hover:bg-bluegrey700 transition-colors block whitespace-nowrap">Предпроектные работы. Работа с внешними подрядчиками</Link>
                                    </li>
                                    <li>
                                        <Link onClick={() => setSubMenu(false)} to="/directions/projecting" className="rounded-[0.375rem] p-2 hover:bg-blue50 dark:hover:bg-bluegrey700 transition-colors block whitespace-nowrap">Проектная деятельность. Разработка и контроль проектной документации</Link>
                                    </li>
                                    <li>
                                        <Link onClick={() => setSubMenu(false)} to="/directions/bim" className="rounded-[0.375rem] p-2 hover:bg-blue50 dark:hover:bg-bluegrey700 transition-colors block whitespace-nowrap">BIM-моделирование</Link>
                                    </li>
                                    <li>
                                        <Link onClick={() => setSubMenu(false)} to="/directions/realisation" className="rounded-[0.375rem] p-2 hover:bg-blue50 dark:hover:bg-bluegrey700 transition-colors block whitespace-nowrap">Реализация и сопровождение проекта. Разработка рабочей документации</Link>
                                    </li>
                                </ul>
                            </span>
                        </span>
                        <span className={cls.blur} />
                    </li>
                    {itemsList}
                </ul>

                <div className="hidden lg:flex items-center gap-6 justify-end col-span-2 ">
                    {/* <button type="button" title="Поиск" aria-label="Поиск"> */}
                    {/*    <Search className="w-6 h-6 stroke-bluegrey900" /> */}
                    {/* </button> */}
                    <ThemeSwitcher />
                    <Button to={RoutePath.login} text="Войти" Icon={Login} sideLink />
                </div>
                <div className="flex lg:hidden items-center gap-4 ml-auto">
                    <a
                        href={RoutePath.login}
                        className="w-fit block text-17med text-bluegrey900"
                        itemProp="url"
                        title="Войти"
                        aria-label="Войти"
                    >
                        Войти
                    </a>
                    <OpenNavButton />
                </div>
            </nav>
        </header>
    );
});
